import { useState } from "react";
import { Clock, MapPin, Mail, Phone, Send, ExternalLink, Users } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";
import { business, hoursDetails } from "@/data/siteData";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

function ContactForm() {
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const name = form.get("name");
    const email = form.get("email");
    const guests = form.get("guests");
    const message = form.get("message");
    if (!name || !email || !message) {
      toast.error("Please fill in your name, email and a short message.");
      return;
    }
    setSubmitting(true);
    const subject = encodeURIComponent(`Reservation enquiry — ${name}`);
    const body = encodeURIComponent(
      `Name: ${name}\nEmail: ${email}\nNumber of guests: ${guests || "—"}\n\n${message}`
    );
    window.location.href = `mailto:${business.email}?subject=${subject}&body=${body}`;
    setTimeout(() => {
      setSubmitting(false);
      toast.success("Your email client is ready — we’ll reply within 24h.");
      e.target.reset?.();
    }, 700);
  };

  const inputClass =
    "rounded-none bg-transparent border-0 border-b border-[#132A1C]/25 focus-visible:ring-0 focus-visible:border-[#132A1C] px-0 text-[#132A1C] placeholder:text-[#5A7365]/70 h-12";

  return (
    <form
      onSubmit={onSubmit}
      data-testid="contact-form"
      className="space-y-6"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label htmlFor="name" className="text-[11px] tracking-[0.2em] uppercase text-[#5A7365]">Name</Label>
          <Input id="name" name="name" data-testid="contact-name" placeholder="Your name" className={inputClass} />
        </div>
        <div>
          <Label htmlFor="email" className="text-[11px] tracking-[0.2em] uppercase text-[#5A7365]">Email</Label>
          <Input id="email" type="email" name="email" data-testid="contact-email" placeholder="you@example.com" className={inputClass} />
        </div>
      </div>
      <div>
        <Label htmlFor="guests" className="text-[11px] tracking-[0.2em] uppercase text-[#5A7365] flex items-center gap-2">
          <Users size={12} strokeWidth={1.5} /> Number of guests
        </Label>
        <Input id="guests" name="guests" type="text" data-testid="contact-guests" placeholder="e.g. 2 people · Sat 14 Dec, 14:00" className={inputClass} />
      </div>
      <div>
        <Label htmlFor="message" className="text-[11px] tracking-[0.2em] uppercase text-[#5A7365]">Message</Label>
        <Textarea
          id="message"
          name="message"
          data-testid="contact-message"
          rows={4}
          placeholder="Occasion, dietary notes, anything we should know…"
          className="rounded-none bg-transparent border-0 border-b border-[#132A1C]/25 focus-visible:ring-0 focus-visible:border-[#132A1C] px-0 text-[#132A1C] placeholder:text-[#5A7365]/70 resize-none"
        />
      </div>
      <button
        type="submit"
        disabled={submitting}
        data-testid="contact-submit"
        className="group inline-flex items-center gap-3 px-7 py-4 bg-[#132A1C] text-[#F4F0EA] text-[12px] tracking-[0.22em] uppercase hover:bg-[#0D1E13] transition-colors disabled:opacity-60"
      >
        {submitting ? "Sending…" : "Send enquiry"}
        <Send size={15} strokeWidth={1.5} className="transition-transform group-hover:translate-x-1" />
      </button>
    </form>
  );
}

export default function Visit() {
  const ref = useReveal();
  return (
    <section
      id="visit"
      ref={ref}
      data-testid="visit-section"
      className="relative py-20 md:py-28 bg-[#132A1C] text-[#F4F0EA]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 relative">
        <p className="reveal text-[11px] tracking-[0.32em] uppercase text-[#D4A373] mb-5">
          Visit · Reserve · Say hello
        </p>
        <h2 className="reveal font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.08] tracking-tight max-w-3xl">
          Come for the coffee. <span className="italic text-[#D4A373]">Stay</span> for the afternoon.
        </h2>

        <div className="mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left: Hours + Address */}
          <div className="lg:col-span-5 space-y-10">
            <div className="reveal">
              <div className="flex items-center gap-3 mb-5 text-[#D4A373]">
                <Clock size={16} strokeWidth={1.5} />
                <span className="text-[11px] tracking-[0.22em] uppercase">Opening hours</span>
              </div>
              <ul data-testid="hours-list" className="space-y-3">
                {hoursDetails.map((h) => (
                  <li
                    key={h.day}
                    className="flex items-baseline justify-between gap-4 border-b border-[#F4F0EA]/15 pb-3"
                  >
                    <span className="font-serif-display text-lg md:text-xl">{h.day}</span>
                    <span className="text-[#F4F0EA]/85 tracking-wide tabular-nums">{h.time}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-[#F4F0EA]/65 text-sm">
                {business.services} · Hours may vary on holidays — check Google for live updates.
              </p>
            </div>

            <div className="reveal grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div>
                <div className="flex items-center gap-3 mb-3 text-[#D4A373]">
                  <MapPin size={16} strokeWidth={1.5} />
                  <span className="text-[11px] tracking-[0.22em] uppercase">Find us</span>
                </div>
                <p className="font-serif-display text-xl md:text-2xl leading-snug">
                  Hämeentie 30
                  <br />
                  00530 Helsinki
                </p>
                <a
                  href={business.mapLink}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="visit-directions-link"
                  className="mt-3 inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-[#F4F0EA]/80 hover:text-[#D4A373] transition-colors min-h-[40px] py-2"
                >
                  Get directions <ExternalLink size={12} strokeWidth={1.5} />
                </a>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-3 text-[#D4A373]">
                  <Mail size={16} strokeWidth={1.5} />
                  <span className="text-[11px] tracking-[0.22em] uppercase">Contact</span>
                </div>
                <a
                  href={`mailto:${business.email}`}
                  data-testid="contact-email-link"
                  className="block font-serif-display text-xl md:text-2xl hover:text-[#D4A373] transition-colors break-words py-1"
                >
                  {business.email}
                </a>
                <a
                  href={`tel:${business.phoneTel}`}
                  data-testid="contact-phone-link"
                  className="mt-1 inline-flex items-center gap-2 text-[#F4F0EA]/85 hover:text-[#D4A373] transition-colors min-h-[40px] py-2"
                >
                  <Phone size={14} strokeWidth={1.5} /> {business.phone}
                </a>
              </div>
            </div>

            <div className="reveal relative overflow-hidden border border-[#F4F0EA]/15">
              <iframe
                data-testid="google-map"
                title="Cafe Anh on Google Maps — Hämeentie 30, Helsinki"
                src={business.mapEmbed}
                width="100%"
                height="280"
                style={{ border: 0, filter: "grayscale(0.35) contrast(0.95)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Right: Contact form */}
          <div className="lg:col-span-6 lg:col-start-7 reveal">
            <div className="bg-[#F4F0EA] text-[#132A1C] p-7 md:p-10 lg:p-12">
              <h3 className="font-serif-display text-2xl md:text-3xl tracking-tight mb-2">
                Reserve a table or say hi
              </h3>
              <p className="text-[#2C4C3B] mb-7 max-w-md text-[15px]">
                Drop us a quick note for groups, dietary requests, or just to ask
                what's good today. We reply by hand.
              </p>
              <ContactForm />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
